import {Button, Input, List, Modal, Radio, RadioChangeEvent} from 'antd';
import {ReactElement, useMemo, useState} from 'react';

import {Text} from '../../../../../../../../component/text/text';
import {useLocale} from '../../../../../../../../provider/locale/locale-hook';
import {Locale} from '../../../../../../../../provider/locale/localization';
import {useSnackbar} from '../../../../../../../../provider/snackbar/snackbar-hook';
import {ReviewPresenceActionEnum} from '../../../../../../../../service/reviews/review-realtime-presence/review-realtime-presence-type';
import {useReviewPresenceActivity} from '../../../../../../../../service/reviews/review-realtime-presence/use-review-presence-activity-hook';
import {
    useResponsibleUsers,
    useResponsibleUserSelect,
} from '../../../../../../../../service/reviews/reviews-responsible-users';
import {ReviewDataType} from '../../../../../../../../service/reviews/reviews-type';
import {classNames} from '../../../../../../../../util/css';
import {ReviewDataWithCatalogType} from '../../../review-list-type';
import {processBatch} from '../../review-helper';

import * as styles from './responsible-user-modal.scss';

type PropsType = {
    onCancel: () => void;
    onSuccess?: () => void;
    reviewId: number;
    updateReview: (reviewId: number, updateData: Partial<ReviewDataType>) => void;
    initialSelectedUserId?: number;
    reviewData: ReviewDataWithCatalogType;
    selectedReviews?: Array<ReviewDataWithCatalogType>;
};

export function ResponsibleUserModal(props: PropsType): ReactElement {
    const {onCancel, onSuccess, reviewId, updateReview, initialSelectedUserId, reviewData, selectedReviews} = props;
    const [selectedUserId, setSelectedUserId] = useState<number | null>(initialSelectedUserId || null);
    const [searchInputValue, setSearchInputValue] = useState<string>('');
    const {getLocalizedString} = useLocale();

    const {mutateAsync, isLoading} = useResponsibleUserSelect();
    const {data: userList, isLoading: isUserListLoading} = useResponsibleUsers(reviewData);
    const {snackbar} = useSnackbar();

    const presenceActivities = useMemo(() => {
        if (selectedReviews && selectedReviews.length > 1) {
            return selectedReviews.map((review) => ({
                reviewId: review.id,
                brandId: review.brand.id,
                action: ReviewPresenceActionEnum.ResponsibleUser,
            }));
        }

        return [
            {
                reviewId,
                brandId: reviewData.brand.id,
                action: ReviewPresenceActionEnum.ResponsibleUser,
            },
        ];
    }, [reviewData.brand.id, reviewId, selectedReviews]);

    useReviewPresenceActivity(presenceActivities, true);

    function handleRadioGroupChange(event: RadioChangeEvent) {
        setSelectedUserId(event.target.value);
    }

    async function handleOk() {
        if (!selectedUserId) {
            return;
        }

        const selectedUser = userList?.results.find((user) => user.id === selectedUserId);

        if (selectedReviews && selectedReviews.length > 1) {
            const userName = selectedUser
                ? [selectedUser.firstName, selectedUser.lastName].filter(Boolean).join(' ')
                : null;

            await processBatch(selectedReviews, 50, async (review) => {
                await mutateAsync({
                    userId: String(selectedUserId),
                    reviewId: review.id,
                    userName,
                    silent: true,
                });
                updateReview(review.id, {
                    responsibleUser: selectedUser,
                });
            });

            snackbar.success({
                message: <Locale stringKey="REVIEWS__RESPONSIBLE_USER__ASSIGNED" />,
                description: (
                    <Locale
                        stringKey="REVIEWS__RESPONSIBLE_USER__ASSIGNED_DESCRIPTION"
                        valueMap={{name: userName ?? ''}}
                    />
                ),
            });
        } else {
            await mutateAsync({
                userId: String(selectedUserId),
                reviewId,
                userName: selectedUser
                    ? [selectedUser.firstName, selectedUser.lastName].filter(Boolean).join(' ')
                    : null,
            });
            updateReview(reviewId, {
                responsibleUser: selectedUser,
            });
        }

        if (onSuccess) {
            onSuccess();
        } else {
            onCancel();
        }
    }

    const listData = userList?.results.filter(
        (user) => user.fullName?.includes(searchInputValue) || user.email?.includes(searchInputValue)
    );

    return (
        <Modal
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    <Locale stringKey="POPUP__BUTTON__CANCEL" />
                </Button>,
                <Button key="ok" loading={isLoading} onClick={handleOk} type="primary">
                    <Locale stringKey="TEXT__APPLY" />
                </Button>,
            ]}
            loading={isUserListLoading}
            onCancel={onCancel}
            open
            title={<Locale stringKey="REVIEW__DROPDOWN_MENU__APPOINT_RESPONSIBLE" />}
        >
            <Input
                allowClear
                onChange={(event) => setSearchInputValue(event.target.value)}
                placeholder={getLocalizedString('USERS__TABLE__SEARCH__PLACEHOLDER')}
                value={searchInputValue}
            />
            {userList && (
                <Radio.Group
                    className={styles.ResponsibleUser_radioGroup}
                    onChange={handleRadioGroupChange}
                    value={selectedUserId}
                >
                    <List
                        bordered
                        dataSource={listData}
                        loading={isUserListLoading}
                        renderItem={(account) => (
                            <Radio
                                className={classNames(styles.Radio, {
                                    [styles.Radio__selected]: account.id === selectedUserId,
                                })}
                                value={account.id}
                            >
                                <Text bolder>{account.fullName}</Text>
                                <Text block secondary>
                                    {account.email}
                                </Text>
                            </Radio>
                        )}
                        size="small"
                        split={false}
                    />
                </Radio.Group>
            )}
        </Modal>
    );
}
